import { getToken, clearToken } from '../utils/authToken';
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../config/api';

const API_URL = BACKEND_URL;

/**
 * Hook centralizado para gestionar estado de suscripción
 * Evita llamadas duplicadas a la API y mantiene consistencia
 */
export function useSubscription() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const loadedRef = useRef(false);

  const loadSubscriptionStatus = useCallback(async (forceRefresh = false) => {
    const token = getToken();
    if (!token || token === 'guest') {
      setData(null);
      setLoading(false);
      return;
    }

    // Evitar llamadas duplicadas
    if (loadedRef.current && !forceRefresh) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/subscriptions/status`, {
      });
      setData(response.data);
      setError(null);
      loadedRef.current = true;
    } catch (err) {
      console.error('Error loading subscription status:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Abrir portal de facturación
  const openBillingPortal = useCallback(async () => {
    const token = getToken();
    if (!token) return null;

    try {
      const response = await axios.post(
        `${API_URL}/api/subscriptions/billing-portal`,
        {},
        {}
      );

      if (response.data.url) {
        // Stripe Billing Portal envia X-Frame-Options: DENY → no se puede
        // mostrar dentro del iframe del preview de Emergent. Forzar pestana
        // nueva para que SIEMPRE funcione (preview, PWA, deploy, mobile).
        const w = window.open(response.data.url, '_blank', 'noopener,noreferrer');
        if (!w) {
          // Popup bloqueado → fallback navegacion top-level (sale del iframe).
          try { window.top.location.href = response.data.url; }
          catch (_e) { window.location.href = response.data.url; }
        }
        return response.data.url;
      }
    } catch (err) {
      console.error('Error opening billing portal:', err);
      throw err;
    }
    return null;
  }, []);

  // Cargar al montar
  useEffect(() => {
    loadSubscriptionStatus();
  }, [loadSubscriptionStatus]);

  // Resetear cuando cambia el token (login/logout)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        loadedRef.current = false;
        loadSubscriptionStatus();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [loadSubscriptionStatus]);

  return {
    // Estado
    data,
    loading,
    error,
    isPremium: data?.is_premium || false,
    planName: data?.plan_name || 'Free',
    usage: data?.usage || {},
    limits: data?.limits || {},
    bonus: data?.bonus || {},
    
    // Acciones
    refresh: () => loadSubscriptionStatus(true),
    openBillingPortal
  };
}

export default useSubscription;
